/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */
import {ActionTree} from 'vuex';
import {
  GameInterface,
  GamePlatformInterface,
  GamePlatformSubTypeInterface,
  GamesStateInterface, GameVersionInterface,
} from '@/store/modules/games/types';
import {RootState} from '@/store/types';
import {api, cms} from '@/services/http';
import {AxiosError, AxiosResponse} from 'axios';

/**
 * Games Actions
 *
 * @author nchapman@frontier.co.uk
 */
export const actions: ActionTree<GamesStateInterface, RootState> = {
  /**
   * Fetch GamesNavigation
   *
   * @param dispatch
   * @param {any} commit
   */
  async fetchGames({dispatch, commit}: { commit: any, dispatch: any}): Promise<void> {
    const platforms: GamePlatformInterface[] = await dispatch('fetchPlatforms');
    return api.axios.get('api/gamesEnabled', {
      params: {
        skipAuthorization: true,
      },
    }).then((response: AxiosResponse) => {
      const payload: GameInterface[] = [];
      const data: any = response.data['hydra:member'];
      for (const game of data) {
        payload.push({
          id: game.id,
          name: game.name,
          slug: game.slug,
          platforms: (platforms || []).filter((obj: GamePlatformInterface) => {
            if (typeof game.platforms === 'undefined' || game.platforms === '') {
              return '';
            }
            return game.platforms.includes(String(obj.id));
          }),
          versions: game.versions || [],
          logo: game.logo,
          icon: game.icon,
          key_art: game.key_art,
          featured: game.featured,
          enabled: game.enabled,
          categories: game.categories,
          readonly: game.readonly,
          use_loaded_via: game.use_loaded_via,
        });
      }
      commit('gamesLoaded', payload);
    }, (err: AxiosError) => {
      commit('gamesError');
    });
  },

  /**
   * Update current game version
   *
   * @param commit
   * @param version
   */
  updateCurrentGameVersion({commit}: { commit: any }, version: GameInterface ): void {
    commit('updateCurrentGameVersion', version);
  },

  /**
   * Update current game
   *
   * @param {any} commit
   * @param game
   */
  updateCurrentGame({commit}: { commit: any }, game: GameInterface | undefined): void {
    commit('updateCurrentGame', game);
  },

  /**
   * Fetch Platforms
   *
   * @param {any} commit
   */
  async fetchPlatforms({commit}: { commit: any }): Promise<GamePlatformInterface[]> {
    const payload: GamePlatformInterface[] = [];
    await cms.axios.get('api/platforms', {
      cache: true,
    }).then(
      async (response: AxiosResponse) => {
        for (const platform of response.data) {
          let subTypes: GamePlatformSubTypeInterface[] = [];

          if (platform.subType.length > 0) {
            const subTypeResponse: AxiosResponse = await cms.axios.get('api/platforms/sub-types', {
              params: {
                id: platform.subType,
              },
              cache: true,
            });
            subTypes = subTypeResponse.data;
          }

          payload.push({
            acronym: platform.id,
            id: +platform.id,
            logo: platform.logo,
            name: platform.name,
            slug: platform.slug,
            types: subTypes,
          });
        }
        commit('updatePlatforms', payload);
      });
    return payload;
  },
};
